import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    Box,
    Typography,
    Card,
    Stack,
    Tabs,
    Tab,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Alert,
    Snackbar,
    InputAdornment,
    Tooltip,
    Fade,
    Paper,
    Divider,
    LinearProgress
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    AccountTree as TreeIcon,
    LocationOn as LocationIcon,
    Settings as SettingsIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
} from "@mui/icons-material";
import axios from "axios";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import AppHeader from "../../components/AppHeader";

const OrgMaster = ({ setIsLoggedIn }) => {
    const [tabValue, setTabValue] = useState(0);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [statusMsg, setStatusMsg] = useState({ open: false, msg: '', severity: 'success' });

    // Data State
    const [orgTypes, setOrgTypes] = useState([]);
    const [orgNodes, setOrgNodes] = useState([]);
    const [branches, setBranches] = useState([]);
    const [somConfig, setSomConfig] = useState({ active_levels_json: [], use_branches: 1, use_job_catalogue: 1 });

    // Dialog State
    const [nodeDialogOpen, setNodeDialogOpen] = useState(false);
    const [editingNode, setEditingNode] = useState(null);
    const [newNode, setNewNode] = useState({ type_id: "", parent_id: "", node_code: "", node_name: "", description: "", is_active: 1 });

    const [branchDialogOpen, setBranchDialogOpen] = useState(false);
    const [editingBranch, setEditingBranch] = useState(null);
    const [newBranch, setNewBranch] = useState({ branch_code: "", branch_name: "", address: "", is_active: 1 });

    const tenant = localStorage.getItem("tenant") || "";

    const fetchNodes = useCallback(async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/som/nodes?include_inactive=1`, {
                withCredentials: true,
                headers: { "X-Tenant": tenant },
            });
            if (res.data.success) {
                const sortedNodes = [...res.data.data].sort((a, b) => {
                    const nameCompare = a.node_name.localeCompare(b.node_name);
                    if (nameCompare !== 0) return nameCompare;
                    return (a.level_order || 0) - (b.level_order || 0);
                });
                setOrgNodes(sortedNodes);
            }
        } catch (err) {
            console.error("Fetch nodes error:", err);
        }
    }, [tenant]);

    const fetchInitialData = useCallback(async () => {
        setLoading(true);
        try {
            const headers = { "X-Tenant": tenant };
            const [typesRes, configRes, branchesRes] = await Promise.all([
                axios.get(`${process.env.REACT_APP_API_URL1}/som/types`, { withCredentials: true, headers }),
                axios.get(`${process.env.REACT_APP_API_URL1}/som/config`, { withCredentials: true, headers }),
                axios.get(`${process.env.REACT_APP_API_URL1}/som/branches?include_inactive=1`, { withCredentials: true, headers }),
            ]);

            if (typesRes.data.success) setOrgTypes(typesRes.data.data);
            if (configRes.data.success) {
                const config = configRes.data.data;
                if (typeof config.active_levels_json === 'string') {
                    config.active_levels_json = JSON.parse(config.active_levels_json);
                }
                setSomConfig(config);
            }
            if (branchesRes.data.success) setBranches(branchesRes.data.data);

            await fetchNodes();
        } catch (err) {
            setStatusMsg({ open: true, msg: "Failed to fetch organization data", severity: "error" });
        } finally {
            setLoading(false);
        }
    }, [tenant, fetchNodes]);

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    const handleSaveNode = useCallback(async () => {
        if (!newNode.node_name || !newNode.type_id) {
            return setStatusMsg({ open: true, msg: "Please fill required fields", severity: "warning" });
        }
        setLoading(true);
        try {
            const headers = { "X-Tenant": tenant };
            if (editingNode) {
                await axios.put(`${process.env.REACT_APP_API_URL1}/som/nodes/${editingNode.id}`, newNode, { withCredentials: true, headers });
            } else {
                await axios.post(`${process.env.REACT_APP_API_URL1}/som/nodes`, newNode, { withCredentials: true, headers });
            }
            setStatusMsg({ open: true, msg: "Node saved successfully", severity: "success" });
            setNodeDialogOpen(false);
            fetchNodes();
        } catch (err) {
            setStatusMsg({ open: true, msg: err.response?.data?.message || "Error saving node", severity: "error" });
        } finally {
            setLoading(false);
        }
    }, [tenant, editingNode, newNode, fetchNodes]);

    const handleSaveBranch = useCallback(async () => {
        if (!newBranch.branch_name || !newBranch.branch_code) {
            return setStatusMsg({ open: true, msg: "Please fill required fields", severity: "warning" });
        }
        setLoading(true);
        try {
            const headers = { "X-Tenant": tenant };
            if (editingBranch) {
                await axios.put(`${process.env.REACT_APP_API_URL1}/som/branches/${editingBranch.id}`, newBranch, { withCredentials: true, headers });
            } else {
                await axios.post(`${process.env.REACT_APP_API_URL1}/som/branches`, newBranch, { withCredentials: true, headers });
            }
            setStatusMsg({ open: true, msg: "Branch saved successfully", severity: "success" });
            setBranchDialogOpen(false);
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/som/branches?include_inactive=1`, { withCredentials: true, headers });
            setBranches(res.data.data);
        } catch (err) {
            setStatusMsg({ open: true, msg: err.response?.data?.message || "Error saving branch", severity: "error" });
        } finally {
            setLoading(false);
        }
    }, [tenant, editingBranch, newBranch]);

    const updateConfig = useCallback(async (updatedConfig) => {
        setLoading(true);
        try {
            await axios.post(`${process.env.REACT_APP_API_URL1}/som/config`, updatedConfig, {
                withCredentials: true,
                headers: { "X-Tenant": tenant },
            });
            setSomConfig(updatedConfig);
            setStatusMsg({ open: true, msg: "Configuration updated", severity: "success" });
        } catch (err) {
            setStatusMsg({ open: true, msg: "Failed to update config", severity: "error" });
        } finally {
            setLoading(false);
        }
    }, [tenant]);

    const toggleLevel = useCallback((levelName) => {
        let newLevels = [...somConfig.active_levels_json];
        if (newLevels.includes(levelName)) {
            newLevels = newLevels.filter(l => l !== levelName);
        } else {
            newLevels.push(levelName);
        }
        updateConfig({ ...somConfig, active_levels_json: newLevels });
    }, [somConfig, updateConfig]);

    const toggleFeature = useCallback((feature) => {
        updateConfig({ ...somConfig, [feature]: somConfig[feature] ? 0 : 1 });
    }, [somConfig, updateConfig]);

    // Grid Renderers
    const actionsRenderer = useCallback((type, params) => (
        <Stack direction="row" spacing={1} sx={{ height: '100%', alignItems: 'center' }}>
            <Tooltip title="Edit">
                <IconButton size="small" sx={{ color: 'primary.main' }} onClick={() => {
                    if (type === 'node') {
                        setEditingNode(params.data);
                        setNewNode(params.data);
                        setNodeDialogOpen(true);
                    } else {
                        setEditingBranch(params.data);
                        setNewBranch(params.data);
                        setBranchDialogOpen(true);
                    }
                }}>
                    <EditIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Stack>
    ), []);

    const statusRenderer = useCallback((params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: params.value ? '#10b981' : '#94a3b8', mr: 1 }} />
            <Typography variant="body2" sx={{ color: params.value ? '#059669' : '#64748b', fontWeight: 600 }}>
                {params.value ? 'Active' : 'Inactive'}
            </Typography>
        </Box>
    ), []);

    const commonColDef = useMemo(() => ({
        sortable: true, filter: true, resizable: true, headerClass: 'premium-grid-header'
    }), []);

    const columnDefs = useMemo(() => ({
        nodes: [
            { field: "type_id", headerName: "Level Type", width: 150, valueGetter: p => orgTypes.find(t => t.id === p.data.type_id)?.type_name || 'N/A' },
            { field: "node_code", headerName: "Code", width: 120, cellStyle: { fontWeight: 'bold', color: 'primary.main' } },
            { field: "node_name", headerName: "Name", flex: 1, cellStyle: { fontWeight: 600 } },
            { field: "parent_id", headerName: "Parent", width: 180, valueGetter: p => orgNodes.find(n => n.id === p.data.parent_id)?.node_name || "-" },
            { field: "is_active", headerName: "Status", width: 120, cellRenderer: statusRenderer },
            { headerName: "Actions", width: 80, cellRenderer: p => actionsRenderer('node', p) }
        ],
        branches: [
            { field: "branch_code", headerName: "Code", width: 120, cellStyle: { fontWeight: 'bold', color: 'primary.main' } },
            { field: "branch_name", headerName: "Branch Name", flex: 1, cellStyle: { fontWeight: 600 } },
            { field: "address", headerName: "Location", flex: 1 },
            { field: "is_active", headerName: "Status", width: 120, cellRenderer: statusRenderer },
            { headerName: "Actions", width: 80, cellRenderer: p => actionsRenderer('branch', p) }
        ]
    }), [orgTypes, orgNodes, statusRenderer, actionsRenderer]);

    const activeRows = useMemo(() => {
        const dataArr = tabValue === 0 ? orgNodes : tabValue === 1 ? branches : [];
        if (!searchText) return dataArr;
        const s = searchText.toLowerCase();
        return dataArr.filter(item => 
            Object.values(item).some(val => String(val).toLowerCase().includes(s))
        );
    }, [tabValue, orgNodes, branches, searchText]);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", bgcolor: "#f8fafc" }}>
            <AppHeader setIsLoggedIn={setIsLoggedIn} />

            <Box sx={{ p: 4, flexGrow: 1, overflow: "hidden", display: "flex", flexDirection: "column", gap: 3, maxWidth: 1000, mx: 'auto', width: '100%' }}>
                <Fade in timeout={500}>
                    <Card sx={{ p: 3, borderRadius: 4, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.04)', bgcolor: '#fff' }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
                            <Box>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
                                    Standard Organization Master
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                                    Manage your corporate hierarchy levels, business units, and branch locations. 
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={1.5}>
                                <TextField
                                    size="small"
                                    placeholder="Quick search..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                                            </InputAdornment>
                                        ),
                                        sx: { borderRadius: 2, bgcolor: '#f1f5f9', border: 'none', '& fieldset': { border: 'none' } }
                                    }}
                                />
                                <Tooltip title="Refresh Data">
                                    <IconButton onClick={fetchInitialData} sx={{ bgcolor: '#f1f5f9' }}><RefreshIcon /></IconButton>
                                </Tooltip>
                                {tabValue < 2 && (
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        sx={{ 
                                            bgcolor: 'primary.main', 
                                            borderRadius: 2, 
                                            px: 3, 
                                            textTransform: 'none', 
                                            fontWeight: 700,
                                            boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}33`,
                                            '&:hover': { bgcolor: 'primary.dark' }
                                        }}
                                        onClick={() => {
                                            if (tabValue === 0) {
                                                setEditingNode(null);
                                                setNewNode({ type_id: "", parent_id: "", node_code: "", node_name: "", description: "", is_active: 1 });
                                                setNodeDialogOpen(true);
                                            } else {
                                                setEditingBranch(null);
                                                setNewBranch({ branch_code: "", branch_name: "", address: "", is_active: 1 });
                                                setBranchDialogOpen(true);
                                            }
                                        }}
                                    >
                                        Add {tabValue === 0 ? 'Node' : 'Branch'}
                                    </Button>
                                )}
                            </Stack>
                        </Stack>
                    </Card>
                </Fade>

                <Paper sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 4px 20px 0 rgba(0,0,0,0.03)' }}>
                    <Tabs
                        value={tabValue}
                        onChange={(e, v) => setTabValue(v)}
                        centered
                        sx={{
                            bgcolor: '#fff',
                            borderBottom: '1px solid #f1f5f9',
                            '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0', bgcolor: 'primary.main' },
                            '& .MuiTab-root': { py: 2, fontWeight: 700, color: '#64748b', textTransform: 'none', fontSize: 15, minWidth: 160 },
                            '& .Mui-selected': { color: '#6366f1 !important' }
                        }}
                    >
                        <Tab icon={<TreeIcon />} iconPosition="start" label="Hierarchy Nodes" />
                        <Tab icon={<LocationIcon />} iconPosition="start" label="Branches" />
                        <Tab icon={<SettingsIcon />} iconPosition="start" label="SOM Configuration" />
                    </Tabs>

                    <Box sx={{ height: 'calc(100vh - 380px)', p: tabValue === 2 ? 4 : 1, bgcolor: '#fff', overflowY: tabValue === 2 ? 'auto' : 'hidden' }}>
                        {loading && <LinearProgress sx={{ borderRadius: 1, mb: 1, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: 'primary.main' } }} />}
                        
                        {tabValue < 2 ? (
                            <Box className="ag-theme-quartz" sx={{ height: '100%', width: '100%', '& .premium-grid-header': { fontWeight: 800, color: '#475569' } }}>
                                <AgGridReact
                                    rowData={activeRows}
                                    columnDefs={tabValue === 0 ? columnDefs.nodes : columnDefs.branches}
                                    defaultColDef={commonColDef}
                                    rowHeight={52}
                                    animateRows={true}
                                    pagination={true}
                                    paginationPageSize={20}
                                />
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Hierarchy Step Configuration</Typography>
                                    <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
                                        Toggle organizational levels to include in your structure. Sequence follows level order defined in master.
                                    </Typography>
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                                        {orgTypes.map(type => (
                                            <Paper 
                                                key={type.id} 
                                                variant="outlined" 
                                                sx={{ 
                                                    px: 2, py: 1, borderRadius: 2, 
                                                    bgcolor: somConfig.active_levels_json.includes(type.type_name) ? '#f5f7ff' : '#fff',
                                                    borderColor: somConfig.active_levels_json.includes(type.type_name) ? 'primary.main' : '#e2e8f0'
                                                }}
                                            >
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={somConfig.active_levels_json.includes(type.type_name)}
                                                            onChange={() => toggleLevel(type.type_name)}
                                                            size="small"
                                                            sx={{ color: 'primary.main', '&.Mui-checked': { color: 'primary.main' } }}
                                                        />
                                                    }
                                                    label={<Typography variant="body2" sx={{ fontWeight: 600 }}>{type.type_name}</Typography>}
                                                />
                                            </Paper>
                                        ))}
                                    </Box>
                                </Box>

                                <Divider />

                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>Extended Modules</Typography>
                                    <Stack direction="row" spacing={4}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={!!somConfig.use_branches}
                                                    onChange={() => toggleFeature('use_branches')}
                                                    sx={{ color: 'primary.main', '&.Mui-checked': { color: 'primary.main' } }}
                                                />
                                            }
                                            label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Physical Branch Tracking</Typography>}
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={!!somConfig.use_job_catalogue}
                                                    onChange={() => toggleFeature('use_job_catalogue')}
                                                    sx={{ color: 'primary.main', '&.Mui-checked': { color: 'primary.main' } }}
                                                />
                                            }
                                            label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Job Family & Title Catalogue</Typography>}
                                        />
                                    </Stack>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Box>

            {/* DIALOGS */}
            <Dialog 
                open={nodeDialogOpen} 
                onClose={() => setNodeDialogOpen(false)} 
                fullWidth maxWidth="sm"
                PaperProps={{ sx: { borderRadius: 4, p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 800 }}>{editingNode ? "Update Node" : "Create Organization Node"}</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2.5 }}>
                        <TextField
                            select label="Selection Level Type" size="small"
                            value={newNode.type_id}
                            onChange={(e) => setNewNode({ ...newNode, type_id: e.target.value })}
                            fullWidth
                        >
                            {orgTypes.map(t => (
                                <MenuItem key={t.id} value={t.id}>{t.type_name} (Level {t.level_order})</MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            select label="Parent Attachment (Optional)" size="small"
                            value={newNode.parent_id}
                            onChange={(e) => setNewNode({ ...newNode, parent_id: e.target.value })}
                            fullWidth
                        >
                            <MenuItem value=""><em>-- No Parent --</em></MenuItem>
                            {orgNodes.filter(n => n.id !== editingNode?.id).map(n => (
                                <MenuItem key={n.id} value={n.id}>{n.node_name} ({orgTypes.find(t => t.id === n.type_id)?.type_name})</MenuItem>
                            ))}
                        </TextField>

                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="Node Code" size="small"
                                value={newNode.node_code}
                                onChange={(e) => setNewNode({ ...newNode, node_code: e.target.value.toUpperCase() })}
                                fullWidth
                                placeholder="e.g. DEPT-01"
                            />
                            <TextField
                                label="Node Name" size="small"
                                value={newNode.node_name}
                                onChange={(e) => setNewNode({ ...newNode, node_name: e.target.value })}
                                fullWidth
                                placeholder="e.g. Administration"
                            />
                        </Stack>

                        <TextField
                            label="Internal Description" size="small" multiline rows={2}
                            value={newNode.description}
                            onChange={(e) => setNewNode({ ...newNode, description: e.target.value })}
                            fullWidth
                            placeholder="Briefly describe this business unit..."
                        />
                        
                        <Paper sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #f1f5f9' }}>
                            <FormControlLabel
                                control={
                                    <Checkbox 
                                        checked={!!newNode.is_active} 
                                        onChange={e => setNewNode({ ...newNode, is_active: e.target.checked ? 1 : 0 })}
                                        sx={{ color: 'primary.main', '&.Mui-checked': { color: 'primary.main' } }}
                                    />
                                }
                                label={<Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>Active Hierarchy Record</Typography>}
                                sx={{ ml: 0 }}
                            />
                        </Paper>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1 }}>
                    <Button onClick={() => setNodeDialogOpen(false)} sx={{ color: '#64748b', fontWeight: 600, textTransform: 'none' }}>Cancel</Button>
                    <Button 
                        variant="contained" onClick={handleSaveNode}
                        sx={{ 
                            bgcolor: 'primary.main', borderRadius: 2, px: 3, fontWeight: 700, textTransform: 'none',
                            '&:hover': { bgcolor: 'primary.dark' }
                        }}
                    >
                        {editingNode ? 'Confirm Update' : 'Create Node'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog 
                open={branchDialogOpen} 
                onClose={() => setBranchDialogOpen(false)} 
                fullWidth maxWidth="sm"
                PaperProps={{ sx: { borderRadius: 4, p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 800 }}>{editingBranch ? "Update Branch" : "Register New Branch"}</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2.5 }}>
                        <TextField
                            label="Branch Reference Code" size="small"
                            value={newBranch.branch_code}
                            onChange={(e) => setNewBranch({ ...newBranch, branch_code: e.target.value.toUpperCase() })}
                            fullWidth
                            placeholder="e.g. BR-YGN"
                        />
                        <TextField
                            label="Official Branch Name" size="small"
                            value={newBranch.branch_name}
                            onChange={(e) => setNewBranch({ ...newBranch, branch_name: e.target.value })}
                            fullWidth
                            placeholder="e.g. Yangon Head Office"
                        />
                        <TextField
                            label="Physical Address" size="small" multiline rows={2}
                            value={newBranch.address}
                            onChange={(e) => setNewBranch({ ...newBranch, address: e.target.value })}
                            fullWidth
                            placeholder="Full street address..."
                        />
                        <Paper sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #f1f5f9' }}>
                            <FormControlLabel
                                control={
                                    <Checkbox 
                                        checked={!!newBranch.is_active} 
                                        onChange={e => setNewBranch({ ...newBranch, is_active: e.target.checked ? 1 : 0 })}
                                        sx={{ color: 'primary.main', '&.Mui-checked': { color: 'primary.main' } }}
                                    />
                                }
                                label={<Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>Active Operation Branch</Typography>}
                                sx={{ ml: 0 }}
                            />
                        </Paper>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1 }}>
                    <Button onClick={() => setBranchDialogOpen(false)} sx={{ color: '#64748b', fontWeight: 600, textTransform: 'none' }}>Cancel</Button>
                    <Button 
                        variant="contained" onClick={handleSaveBranch}
                        sx={{ 
                            bgcolor: 'primary.main', borderRadius: 2, px: 3, fontWeight: 700, textTransform: 'none',
                            '&:hover': { bgcolor: 'primary.dark' }
                        }}
                    >
                        {editingBranch ? 'Confirm Update' : 'Register Branch'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={statusMsg.open}
                autoHideDuration={4000}
                onClose={() => setStatusMsg({ ...statusMsg, open: false })}
                TransitionComponent={Fade}
            >
                <Alert 
                    severity={statusMsg.severity} 
                    variant="filled" 
                    sx={{ width: '100%', borderRadius: 3, fontWeight: 600, boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                >
                    {statusMsg.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default OrgMaster;
